import React, { useMemo, useState } from 'react';
import { useOrganization } from '../contexts/OrganizationContext';
import { ImageIcon, PlusIcon, SearchIcon, FilterIcon, DownloadIcon, TrashIcon, EditIcon, XIcon, CheckIcon, UploadIcon, TagIcon } from 'lucide-react';
import { MOCK_IMAGES } from '../schemas/mockData';
import type { ImageMetadata, Domain, Level, AgeBand, EmotionalCategory } from '../schemas/types';
import { SearchInput } from '../components/SearchInput';
/**
 * Image Library Page
 * Comprehensive image management with promotion/prevention classification
 * Supports tag-based organization and advanced filtering
 */
export function ImageLibrary() {
  const {
    t
  } = useOrganization();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDomain, setSelectedDomain] = useState<Domain | 'all'>('all');
  const [selectedLevel, setSelectedLevel] = useState<Level | 'all'>('all');
  const [selectedAgeBand, setSelectedAgeBand] = useState<AgeBand | 'all'>('all');
  const [selectedEmotionalCategory, setSelectedEmotionalCategory] = useState<EmotionalCategory | 'all'>('all');
  const [showFilters, setShowFilters] = useState(true);
  const [selectedImages, setSelectedImages] = useState<Set<string>>(new Set());
  // Upload modal state
  const [showUploadModal, setShowUploadModal] = useState(false);
  // Edit modal state
  const [editingImage, setEditingImage] = useState<ImageMetadata | null>(null);
  // Filter images based on all criteria
  const filteredImages = useMemo(() => {
    return MOCK_IMAGES.filter(image => {
      // Search filter
      const matchesSearch = searchQuery === '' || image.filename.toLowerCase().includes(searchQuery.toLowerCase()) || image.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase())) || image.id.toLowerCase().includes(searchQuery.toLowerCase());
      // Domain filter
      const matchesDomain = selectedDomain === 'all' || image.domain === selectedDomain;
      // Level filter
      const matchesLevel = selectedLevel === 'all' || image.level === selectedLevel;
      // Age band filter
      const matchesAgeBand = selectedAgeBand === 'all' || image.ageBands.includes(selectedAgeBand);
      // Emotional category filter (NEW)
      const matchesEmotionalCategory = selectedEmotionalCategory === 'all' || image.emotionalCategory === selectedEmotionalCategory;
      return matchesSearch && matchesDomain && matchesLevel && matchesAgeBand && matchesEmotionalCategory;
    });
  }, [searchQuery, selectedDomain, selectedLevel, selectedAgeBand, selectedEmotionalCategory]);
  // Statistics
  const stats = useMemo(() => {
    return {
      total: MOCK_IMAGES.length,
      filtered: filteredImages.length,
      promotion: MOCK_IMAGES.filter(img => img.emotionalCategory === 'promotion').length,
      prevention: MOCK_IMAGES.filter(img => img.emotionalCategory === 'prevention').length,
      domains: {
        Self: MOCK_IMAGES.filter(img => img.domain === 'Self').length,
        Material: MOCK_IMAGES.filter(img => img.domain === 'Material').length,
        Social: MOCK_IMAGES.filter(img => img.domain === 'Social').length,
        Spiritual: MOCK_IMAGES.filter(img => img.domain === 'Spiritual').length
      }
    };
  }, [filteredImages]);
  const handleSelectImage = (imageId: string) => {
    const newSelected = new Set(selectedImages);
    if (newSelected.has(imageId)) {
      newSelected.delete(imageId);
    } else {
      newSelected.add(imageId);
    }
    setSelectedImages(newSelected);
  };
  const handleSelectAll = () => {
    if (selectedImages.size === filteredImages.length) {
      setSelectedImages(new Set());
    } else {
      setSelectedImages(new Set(filteredImages.map(img => img.id)));
    }
  };
  const clearFilters = () => {
    setSearchQuery('');
    setSelectedDomain('all');
    setSelectedLevel('all');
    setSelectedAgeBand('all');
    setSelectedEmotionalCategory('all');
  };
  const hasActiveFilters = searchQuery !== '' || selectedDomain !== 'all' || selectedLevel !== 'all' || selectedAgeBand !== 'all' || selectedEmotionalCategory !== 'all';
  return <div className="p-8 max-w-7xl mx-auto" data-id="element-1157">
      {/* Header */}
      <div className="mb-8" data-id="element-1158">
        <div className="flex items-center justify-between mb-3" data-id="element-1159">
          <div className="flex items-center gap-3" data-id="element-1160">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg" data-id="element-1161">
              <ImageIcon className="w-7 h-7 text-white" data-id="element-1162" />
            </div>
            <div data-id="element-1163">
              <h1 className="text-3xl font-bold text-gray-900" data-id="element-1164">
                Image Library
              </h1>
              <p className="text-gray-600 mt-1" data-id="element-1165">
                Manage and organize assessment images with emotional
                classification
              </p>
            </div>
          </div>
          <button onClick={() => setShowUploadModal(true)} className="px-6 py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-xl hover:from-purple-700 hover:to-purple-800 transition-all font-semibold shadow-lg flex items-center gap-2" data-id="element-1166">
            <PlusIcon className="w-5 h-5" data-id="element-1167" />
            Upload Images
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6" data-id="element-1168">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border-2 border-blue-200" data-id="element-1169">
          <div className="text-sm font-semibold text-blue-900 mb-1" data-id="element-1170">
            Total Images
          </div>
          <div className="text-2xl font-bold text-blue-700" data-id="element-1171">{stats.total}</div>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border-2 border-green-200" data-id="element-1172">
          <div className="text-sm font-semibold text-green-900 mb-1" data-id="element-1173">
            Promotion
          </div>
          <div className="text-2xl font-bold text-green-700" data-id="element-1174">
            {stats.promotion}
          </div>
        </div>
        <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4 border-2 border-orange-200" data-id="element-1175">
          <div className="text-sm font-semibold text-orange-900 mb-1" data-id="element-1176">
            Prevention
          </div>
          <div className="text-2xl font-bold text-orange-700" data-id="element-1177">
            {stats.prevention}
          </div>
        </div>
        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border-2 border-purple-200" data-id="element-1178">
          <div className="text-sm font-semibold text-purple-900 mb-1" data-id="element-1179">Self</div>
          <div className="text-2xl font-bold text-purple-700" data-id="element-1180">
            {stats.domains.Self}
          </div>
        </div>
        <div className="bg-gradient-to-br from-pink-50 to-pink-100 rounded-xl p-4 border-2 border-pink-200" data-id="element-1181">
          <div className="text-sm font-semibold text-pink-900 mb-1" data-id="element-1182">Social</div>
          <div className="text-2xl font-bold text-pink-700" data-id="element-1183">
            {stats.domains.Social}
          </div>
        </div>
        <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-xl p-4 border-2 border-indigo-200" data-id="element-1184">
          <div className="text-sm font-semibold text-indigo-900 mb-1" data-id="element-1185">
            Filtered
          </div>
          <div className="text-2xl font-bold text-indigo-700" data-id="element-1186">
            {stats.filtered}
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-xl shadow-sm border-2 border-gray-200 p-6 mb-6" data-id="element-1187">
        <div className="flex items-center justify-between mb-4" data-id="element-1188">
          <div className="flex items-center gap-2" data-id="element-1189">
            <FilterIcon className="w-5 h-5 text-gray-600" data-id="element-1190" />
            <h2 className="text-lg font-bold text-gray-900" data-id="element-1191">Filters</h2>
            {hasActiveFilters && <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full" data-id="element-1192">
                Active
              </span>}
          </div>
          <div className="flex items-center gap-2" data-id="element-1193">
            {hasActiveFilters && <button onClick={clearFilters} className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-1" data-id="element-1194">
                <XIcon className="w-4 h-4" data-id="element-1195" />
                Clear
              </button>}
            <button onClick={() => setShowFilters(!showFilters)} className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1196">
              {showFilters ? 'Hide' : 'Show'}
            </button>
          </div>
        </div>
        {showFilters && <div className="space-y-4" data-id="element-1197">
            {/* Search - NOW USING SHARED COMPONENT */}
            <div data-id="element-1198">
              <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1199">
                Search
              </label>
              <SearchInput value={searchQuery} onChange={setSearchQuery} placeholder="Search by filename, ID, or tags..." data-id="element-1200" />
            </div>

            {/* Filter Grid */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-1201">
              {/* Emotional Category Filter */}
              <div data-id="element-1202">
                <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1203">
                  Emotional Category
                </label>
                <select value={selectedEmotionalCategory} onChange={e => setSelectedEmotionalCategory(e.target.value as EmotionalCategory | 'all')} className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500" data-id="element-1204">
                  <option value="all" data-id="element-1205">All Categories</option>
                  <option value="promotion" data-id="element-1206">Promotion</option>
                  <option value="prevention" data-id="element-1207">Prevention</option>
                </select>
              </div>

              {/* Domain Filter */}
              <div data-id="element-1208">
                <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1209">
                  Domain
                </label>
                <select value={selectedDomain} onChange={e => setSelectedDomain(e.target.value as Domain | 'all')} className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500" data-id="element-1210">
                  <option value="all" data-id="element-1211">All Domains</option>
                  <option value="Self" data-id="element-1212">Self</option>
                  <option value="Material" data-id="element-1213">Material</option>
                  <option value="Social" data-id="element-1214">Social</option>
                  <option value="Spiritual" data-id="element-1215">Spiritual</option>
                </select>
              </div>

              {/* Level Filter */}
              <div data-id="element-1216">
                <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1217">
                  Level
                </label>
                <select value={selectedLevel} onChange={e => setSelectedLevel(e.target.value as Level | 'all')} className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500" data-id="element-1218">
                  <option value="all" data-id="element-1219">All Levels</option>
                  <option value="Foundational" data-id="element-1220">Foundational</option>
                  <option value="Experiential" data-id="element-1221">Experiential</option>
                  <option value="Aspirational" data-id="element-1222">Aspirational</option>
                </select>
              </div>

              {/* Age Band Filter */}
              <div data-id="element-1223">
                <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1224">
                  Age Band
                </label>
                <select value={selectedAgeBand} onChange={e => setSelectedAgeBand(e.target.value as AgeBand | 'all')} className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500" data-id="element-1225">
                  <option value="all" data-id="element-1226">All Age Bands</option>
                  <option value="5-9" data-id="element-1227">5-9 years</option>
                  <option value="10-14" data-id="element-1228">10-14 years</option>
                  <option value="14-18" data-id="element-1229">14-18 years</option>
                  <option value="Adults" data-id="element-1230">Adults</option>
                  <option value="Students" data-id="element-1231">Students</option>
                </select>
              </div>
            </div>
          </div>}
      </div>

      {/* Bulk Actions */}
      {selectedImages.size > 0 && <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 mb-6 flex items-center justify-between" data-id="element-1232">
          <div className="flex items-center gap-3" data-id="element-1233">
            <CheckIcon className="w-5 h-5 text-blue-600" data-id="element-1234" />
            <span className="font-semibold text-blue-900" data-id="element-1235">
              {selectedImages.size} image{selectedImages.size !== 1 ? 's' : ''}{' '}
              selected
            </span>
          </div>
          <div className="flex items-center gap-2" data-id="element-1236">
            <button className="px-4 py-2 bg-white border-2 border-blue-200 text-blue-700 rounded-lg hover:bg-blue-50 transition-colors font-semibold flex items-center gap-2" data-id="element-1237">
              <DownloadIcon className="w-4 h-4" data-id="element-1238" />
              Download
            </button>
            <button className="px-4 py-2 bg-white border-2 border-red-200 text-red-700 rounded-lg hover:bg-red-50 transition-colors font-semibold flex items-center gap-2" data-id="element-1239">
              <TrashIcon className="w-4 h-4" data-id="element-1240" />
              Delete
            </button>
          </div>
        </div>}

      {/* Image Grid */}
      <div className="bg-white rounded-xl shadow-sm border-2 border-gray-200 p-6" data-id="element-1241">
        <div className="flex items-center justify-between mb-6" data-id="element-1242">
          <h2 className="text-lg font-bold text-gray-900" data-id="element-1243">
            {filteredImages.length} Image
            {filteredImages.length !== 1 ? 's' : ''}
          </h2>
          <button onClick={handleSelectAll} className="text-sm text-purple-600 hover:text-purple-700 font-semibold" data-id="element-1244">
            {selectedImages.size === filteredImages.length ? 'Deselect All' : 'Select All'}
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-id="element-1245">
          {filteredImages.map(image => {
          const isSelected = selectedImages.has(image.id);
          return <div key={image.id} className={`relative group rounded-xl overflow-hidden border-2 transition-all cursor-pointer ${isSelected ? 'border-purple-500 shadow-lg' : 'border-gray-200 hover:border-purple-300 hover:shadow-md'}`} onClick={() => handleSelectImage(image.id)} data-id="element-1246">
                {/* Image */}
                <div className="aspect-square bg-gray-100 relative overflow-hidden" data-id="element-1247">
                  {image.url ? <img src={image.thumbnailUrl || image.url} alt={image.filename} className="w-full h-full object-cover" data-id="element-1248" /> : <div className="w-full h-full flex items-center justify-center" data-id="element-1249">
                      <ImageIcon className="w-12 h-12 text-gray-400" data-id="element-1250" />
                    </div>}
                  {/* Selection Overlay */}
                  {isSelected && <div className="absolute inset-0 bg-purple-600/20 flex items-center justify-center" data-id="element-1251">
                      <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center" data-id="element-1252">
                        <CheckIcon className="w-5 h-5 text-white" data-id="element-1253" />
                      </div>
                    </div>}
                  {/* Emotional Category Badge */}
                  <div className={`absolute top-2 left-2 px-2 py-1 rounded-full text-xs font-bold ${image.emotionalCategory === 'promotion' ? 'bg-green-500 text-white' : 'bg-orange-500 text-white'}`} data-id="element-1254">
                    {image.emotionalCategory === 'promotion' ? 'Promotion' : 'Prevention'}
                  </div>
                </div>

                {/* Info */}
                <div className="p-3 bg-white" data-id="element-1255">
                  <div className="text-sm font-semibold text-gray-900 truncate mb-1" data-id="element-1256">
                    {image.filename}
                  </div>
                  <div className="flex items-center gap-1 mb-2" data-id="element-1257">
                    <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-semibold rounded" data-id="element-1258">
                      {image.domain}
                    </span>
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-semibold rounded" data-id="element-1259">
                      {image.level}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 font-mono" data-id="element-1260">
                    {image.id}
                  </div>
                </div>

                {/* Actions */}
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1" data-id="element-1261">
                  <button onClick={e => {
                e.stopPropagation();
                setEditingImage(image);
              }} className="p-2 bg-white rounded-lg shadow-lg hover:bg-gray-50 transition-colors" data-id="element-1262">
                    <EditIcon className="w-4 h-4 text-gray-700" data-id="element-1263" />
                  </button>
                  <button onClick={e => {
                e.stopPropagation();
              }} className="p-2 bg-white rounded-lg shadow-lg hover:bg-red-50 transition-colors" data-id="element-1264">
                    <TrashIcon className="w-4 h-4 text-red-600" data-id="element-1265" />
                  </button>
                </div>
              </div>;
        })}
        </div>

        {filteredImages.length === 0 && <div className="text-center py-12" data-id="element-1266">
            <ImageIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" data-id="element-1267" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-1268">
              No images found
            </h3>
            <p className="text-gray-600 mb-4" data-id="element-1269">
              {hasActiveFilters ? 'Try adjusting your filters' : 'Upload your first image to get started'}
            </p>
            {hasActiveFilters && <button onClick={clearFilters} className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-semibold" data-id="element-1270">
                Clear Filters
              </button>}
          </div>}
      </div>

      {/* Upload Modal */}
      {showUploadModal && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-id="element-1271">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-id="element-1272">
            <div className="p-6 border-b-2 border-gray-200 flex items-center justify-between sticky top-0 bg-white" data-id="element-1273">
              <div className="flex items-center gap-3" data-id="element-1274">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center" data-id="element-1275">
                  <UploadIcon className="w-6 h-6 text-purple-600" data-id="element-1276" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900" data-id="element-1277">
                  Upload Images
                </h2>
              </div>
              <button onClick={() => setShowUploadModal(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1278">
                <XIcon className="w-6 h-6 text-gray-600" data-id="element-1279" />
              </button>
            </div>
            <div className="p-6" data-id="element-1280">
              <div className="border-2 border-dashed border-gray-300 rounded-xl p-12 text-center hover:border-purple-400 transition-colors cursor-pointer" data-id="element-1281">
                <UploadIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" data-id="element-1282" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-1283">
                  Drop files here or click to browse
                </h3>
                <p className="text-gray-600 text-sm" data-id="element-1284">
                  Supports JPG, PNG, GIF up to 10MB
                </p>
              </div>
              <div className="mt-6 space-y-4" data-id="element-1285">
                <div data-id="element-1286">
                  <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1287">
                    Emotional Category *
                  </label>
                  <select className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500" data-id="element-1288">
                    <option value="" data-id="element-1289">Select category...</option>
                    <option value="promotion" data-id="element-1290">Promotion</option>
                    <option value="prevention" data-id="element-1291">Prevention</option>
                  </select>
                </div>
                <div data-id="element-1292">
                  <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1293">
                    Domain *
                  </label>
                  <select className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500" data-id="element-1294">
                    <option value="" data-id="element-1295">Select domain...</option>
                    <option value="Self" data-id="element-1296">Self</option>
                    <option value="Material" data-id="element-1297">Material</option>
                    <option value="Social" data-id="element-1298">Social</option>
                    <option value="Spiritual" data-id="element-1299">Spiritual</option>
                  </select>
                </div>
                <div data-id="element-1300">
                  <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1301">
                    Level *
                  </label>
                  <select className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500" data-id="element-1302">
                    <option value="" data-id="element-1303">Select level...</option>
                    <option value="Foundational" data-id="element-1304">Foundational</option>
                    <option value="Experiential" data-id="element-1305">Experiential</option>
                    <option value="Aspirational" data-id="element-1306">Aspirational</option>
                  </select>
                </div>
                <div data-id="element-1307">
                  <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1308">
                    Tags
                  </label>
                  <input type="text" placeholder="Add tags separated by commas..." className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500" data-id="element-1309" />
                </div>
              </div>
            </div>
            <div className="p-6 border-t-2 border-gray-200 flex items-center justify-end gap-3 sticky bottom-0 bg-white" data-id="element-1310">
              <button onClick={() => setShowUploadModal(false)} className="px-6 py-2 border-2 border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-semibold" data-id="element-1311">
                Cancel
              </button>
              <button className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-semibold" data-id="element-1312">
                Upload Images
              </button>
            </div>
          </div>
        </div>}

      {/* Edit Modal */}
      {editingImage && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-id="element-1313">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-id="element-1314">
            <div className="p-6 border-b-2 border-gray-200 flex items-center justify-between sticky top-0 bg-white" data-id="element-1315">
              <div className="flex items-center gap-3" data-id="element-1316">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center" data-id="element-1317">
                  <EditIcon className="w-6 h-6 text-blue-600" data-id="element-1318" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900" data-id="element-1319">Edit Image</h2>
              </div>
              <button onClick={() => setEditingImage(null)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1320">
                <XIcon className="w-6 h-6 text-gray-600" data-id="element-1321" />
              </button>
            </div>
            <div className="p-6" data-id="element-1322">
              {/* Image Preview */}
              <div className="mb-6" data-id="element-1323">
                <div className="aspect-video bg-gray-100 rounded-xl overflow-hidden" data-id="element-1324">
                  {editingImage.url ? <img src={editingImage.url} alt={editingImage.filename} className="w-full h-full object-contain" data-id="element-1325" /> : <div className="w-full h-full flex items-center justify-center" data-id="element-1326">
                      <ImageIcon className="w-16 h-16 text-gray-400" data-id="element-1327" />
                    </div>}
                </div>
              </div>
              <div className="space-y-4" data-id="element-1328">
                <div data-id="element-1329">
                  <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1330">
                    Filename
                  </label>
                  <input type="text" defaultValue={editingImage.filename} className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-1331" />
                </div>
                <div data-id="element-1332">
                  <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1333">
                    Emotional Category *
                  </label>
                  <select defaultValue={editingImage.emotionalCategory} className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-1334">
                    <option value="promotion" data-id="element-1335">Promotion</option>
                    <option value="prevention" data-id="element-1336">Prevention</option>
                  </select>
                </div>
                <div data-id="element-1337">
                  <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1338">
                    Domain *
                  </label>
                  <select defaultValue={editingImage.domain} className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-1339">
                    <option value="Self" data-id="element-1340">Self</option>
                    <option value="Material" data-id="element-1341">Material</option>
                    <option value="Social" data-id="element-1342">Social</option>
                    <option value="Spiritual" data-id="element-1343">Spiritual</option>
                  </select>
                </div>
                <div data-id="element-1344">
                  <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1345">
                    Level *
                  </label>
                  <select defaultValue={editingImage.level} className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-1346">
                    <option value="Foundational" data-id="element-1347">Foundational</option>
                    <option value="Experiential" data-id="element-1348">Experiential</option>
                    <option value="Aspirational" data-id="element-1349">Aspirational</option>
                  </select>
                </div>
                <div data-id="element-1350">
                  <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1351">
                    Tags
                  </label>
                  <div className="flex flex-wrap gap-2 mb-2" data-id="element-1352">
                    {editingImage.tags.map((tag, index) => <span key={index} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium flex items-center gap-1" data-id="element-1353">
                        <TagIcon className="w-3 h-3" data-id="element-1354" />
                        {tag}
                      </span>)}
                  </div>
                  <input type="text" placeholder="Add new tags separated by commas..." className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-1355" />
                </div>
                <div data-id="element-1356">
                  <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1357">
                    Image ID
                  </label>
                  <input type="text" value={editingImage.id} disabled className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg bg-gray-50 text-gray-500 font-mono text-sm" data-id="element-1358" />
                </div>
              </div>
            </div>
            <div className="p-6 border-t-2 border-gray-200 flex items-center justify-end gap-3 sticky bottom-0 bg-white" data-id="element-1359">
              <button onClick={() => setEditingImage(null)} className="px-6 py-2 border-2 border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-semibold" data-id="element-1360">
                Cancel
              </button>
              <button className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold" data-id="element-1361">
                Save Changes
              </button>
            </div>
          </div>
        </div>}
    </div>;
}